import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CheckCircleIcon,
  TableCellsIcon,
  InformationCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { ApiService } from '../../services/api';
import { ProfileReport, ProfileReportSummary } from '../../types';
import DQRReport from '../DQRReport/DQRReport';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';
import {
  // existing imports
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

interface ProfilingReportProps {
  jobId: number;
  onClose: () => void;
}

interface ReportSection {
  title: string;
  data: any;
  type: 'table' | 'chart' | 'text' | 'json';
}

const ProfilingReport: React.FC<ProfilingReportProps> = ({ jobId, onClose }) => {
  const [reports, setReports] = useState<ProfileReportSummary[]>([]);
  const [selectedReport, setSelectedReport] = useState<ProfileReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('summary');


  useEffect(() => {
    fetchProfileReports();
  }, [jobId]);

  const fetchProfileReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ApiService.getProfileReportsByJobId(jobId);
      setReports(data);

      // Auto-select the first completed report if available
      const completedReport = data.find(r => r.status === 'COMPLETED');
      if (completedReport) {
        await loadFullReport(completedReport.id);
      }
    } catch (err) {
      setError('Failed to load profiling reports');
      console.error('Error fetching profile reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadFullReport = async (reportId: number) => {
    try {
      setLoading(true);
      const fullReport = await ApiService.getProfileReportById(reportId);
      setSelectedReport(fullReport);
      setActiveTab('summary');
    } catch (err) {
      setError('Failed to load full report');
      console.error('Error loading full report:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'FAILED':
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
      case 'INPROGRESS':
        return <ClockIcon className="w-5 h-5 text-blue-500" />;
      default:
        return <InformationCircleIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'FAILED':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'INPROGRESS':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const renderReportContent = () => {
    if (!selectedReport || !selectedReport.report) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No report data available</p>
          </div>
        </div>
      );
    }

    let reportData = selectedReport.report;

    // If report data is still a string, try to parse it
    if (typeof reportData === 'string') {
      try {
        reportData = JSON.parse(reportData);
      } catch (parseError) {
        console.error('Failed to parse report JSON:', parseError);
        return (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <DocumentTextIcon className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <p className="text-red-500">Failed to parse report data</p>
              <p className="text-gray-500 text-sm mt-2">The report data is malformed JSON</p>
            </div>
          </div>
        );
      }
    }

    switch (activeTab) {
      case 'summary':
        return renderSummary(reportData);
      case 'profiling':
        return renderProfiling(reportData);
      case 'structural':
        return renderStructural(reportData);
      case 'description':
        return renderDescription(reportData);
      case 'dqr':
        return<DQRReport isActive={true} />;
      case 'raw':
        return renderRawData(reportData);
      default:
        return renderSummary(reportData);
    }
  };

  const renderSummary = (reportData: any) => {
    const profiling = reportData.profiling || {};
    const structural = reportData.structural || {};
    const description = reportData.description || {};
    const summary = reportData.summary || {};

    // Extract scores and data
    const profilingData = profiling.data || [];
    const structuralData = structural.data || [];
    const descriptionData = description.data || [];

    // Extract scores from summary data (backend returns them as formatted strings)
    let profilingScore = 0;
    let structuralScore = 0;
    let descriptionScore = 0;

    if (summary.data && summary.data.length > 0) {
      const summaryRow = summary.data[0];

      // Parse scores from formatted strings like "2.50/5"
      profilingScore = parseFloat(summaryRow.Data_Profiling_Score?.split('/')[0] || '0');
      structuralScore = parseFloat(summaryRow.Structural_Consistency_Score?.split('/')[0] || '0');
      descriptionScore = parseFloat(summaryRow.Description_Score?.split('/')[0] || '0');

      // Fallback: if Description_Score is not found, try alternative field names
      if (descriptionScore === 0 && summaryRow.Description_Score === undefined) {
        // Try alternative field names that might be used
        const altDescScore = summaryRow.Description_Quality_Score || summaryRow.desc_score || summaryRow.description_score;
        if (altDescScore) {
          descriptionScore = parseFloat(altDescScore.split('/')[0] || '0');
        }
      }
    }

    // Define weightages
    const weightages = {
      profiling: 35,
      structural: 35,
      description: 30
    };

    // Calculate weighted overall score
    const overallScore = (
      (profilingScore * weightages.profiling / 100) +
      (structuralScore * weightages.structural / 100) +
      (descriptionScore * weightages.description / 100)
    );

    // Count total records and fields
    const totalRecords = profilingData.length > 0 ? profilingData[0].Total_Records : 0;
    const totalFields = profilingData.length;

    // Radar chart data
    const radarData = {
      labels: [
        `Profiling (${weightages.profiling}%)`,
        `Structural (${weightages.structural}%)`,
        `Description (${weightages.description}%)`
      ],
      datasets: [
        {
          label: 'Quality Scores',
          data: [profilingScore, structuralScore, descriptionScore],
          backgroundColor: 'rgba(59, 130, 246, 0.2)',
          borderColor: 'rgba(59, 130, 246, 1)',
          borderWidth: 3,
          pointBackgroundColor: 'rgba(59, 130, 246, 1)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 6,
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: 'rgba(59, 130, 246, 1)',
          pointHoverRadius: 8,
          fill: true
        }
      ]
    };

    const radarOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        r: {
          angleLines: {
            display: true,
            color: 'rgba(0, 0, 0, 0.1)'
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.1)'
          },
          pointLabels: {
            font: {
              size: 12,
              weight: 'bold' as const
            },
            color: '#374151'
          },
          min: 0,
          max: 5,
          ticks: {
            stepSize: 1,
            display: true,
            backdropColor: 'transparent',
            color: '#6B7280',
            font: {
              size: 10
            }
          }
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#fff',
          bodyColor: '#fff',
          borderColor: 'rgba(59, 130, 246, 1)',
          borderWidth: 1,
          callbacks: {
            label: function(context: any) {
              return `Score: ${context.parsed.r}/5`;
            }
          }
        }
      },
      elements: {
        line: {
          borderWidth: 3
        },
        point: {
          radius: 6,
          hoverRadius: 8
        }
      }
    } as const;

    return (
      <div className="space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <TableCellsIcon className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-blue-900">Total Records</p>
                <p className="text-2xl font-bold text-blue-600">
                  {totalRecords}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <ChartBarIcon className="w-8 h-8 text-green-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-green-900">Total Fields</p>
                <p className="text-2xl font-bold text-green-600">
                  {totalFields}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center">
              <InformationCircleIcon className="w-8 h-8 text-purple-600 mr-3" />
              <div>
                <p className="text-sm font-medium text-purple-900">Overall Score</p>
                <p className="text-2xl font-bold text-purple-600">
                  {overallScore.toFixed(1)}/5.0
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quality Scores with Weightages */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quality Assessment Breakdown</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm font-medium text-blue-900">Profiling Quality</p>
              <p className="text-2xl font-bold text-blue-600">{profilingScore.toFixed(1)}/5.0</p>
              <p className="text-xs text-blue-700 mt-1">Weight: {weightages.profiling}%</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm font-medium text-green-900">Structural Quality</p>
              <p className="text-2xl font-bold text-green-600">{structuralScore.toFixed(1)}/5.0</p>
              <p className="text-xs text-green-700 mt-1">Weight: {weightages.structural}%</p>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
              <p className="text-sm font-medium text-orange-900">Description Quality</p>
              <p className="text-2xl font-bold text-orange-600">{descriptionScore.toFixed(1)}/5.0</p>
              <p className="text-xs text-orange-700 mt-1">Weight: {weightages.description}%</p>
            </div>
          </div>

          {/* Radar Chart */}
          <div className="h-64">
            <Radar data={radarData} options={radarOptions} />
          </div>
        </div>

        {/* Report Generation Summary */}
        {summary.data && summary.data.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Report Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-900">Report Date</p>
                <p className="text-lg font-semibold text-gray-700">
                  {summary.data[0].Report_Date}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-900">Overall Quality Score</p>
                <p className="text-lg font-semibold text-gray-700">
                  {summary.data[0].Overall_Score}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderProfiling = (reportData: any) => {
    // Handle case where reportData might be null or undefined
    if (!reportData) {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="text-center">
            <ExclamationTriangleIcon className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-yellow-900 mb-2">No Profiling Data Available</h3>
            <p className="text-yellow-700">
              The profiling report data is missing or could not be loaded. Please try again or contact support if the issue persists.
            </p>
          </div>
        </div>
      );
    }

    const profiling = reportData.profiling || {};
    const profilingData = profiling.data || [];

    // Get rule-specific data if available
    const ruleSpecific = reportData.rule_specific || {};
    const ruleSpecificData = ruleSpecific.data || [];

    // Group rule-specific data by rule type
    const ruleGroups: { [key: string]: any[] } = {};
    if (ruleSpecificData.length > 0) {
      ruleSpecificData.forEach((rule: any) => {
        // Skip entries with missing required fields
        if (!rule || (!rule.Field_Name && !rule.Master_Data_Item) || !rule.Rule_Type) {
          console.warn('Skipping malformed rule-specific data entry:', rule);
          return;
        }

        // Use either Field_Name or Master_Data_Item
        const fieldName = rule.Field_Name || rule.Master_Data_Item;
        rule.Field_Name = fieldName; // Ensure Field_Name is set for consistency

        const ruleType = rule.Rule_Type || 'other';
        if (!ruleGroups[ruleType]) {
          ruleGroups[ruleType] = [];
        }

        // Filter out NaN values and convert to readable format
        const processedRule = {...rule};
        if (processedRule.Compliance_Percentage) {
          const complianceValue = parseFloat(processedRule.Compliance_Percentage);
          processedRule.Compliance_Percentage = isNaN(complianceValue) ? 'N/A' : `${complianceValue.toFixed(2)}%`;
        }

        if (processedRule.Total_Records) {
          const totalValue = parseInt(processedRule.Total_Records);
          processedRule.Total_Records = isNaN(totalValue) ? 'N/A' : totalValue;
        }

        if (processedRule.Valid_Records) {
          const validValue = parseInt(processedRule.Valid_Records);
          processedRule.Valid_Records = isNaN(validValue) ? 'N/A' : validValue;
        }

        ruleGroups[ruleType].push(processedRule);
      });
    }

    // Helper function to get color theme for each rule type
    const getRuleTypeTheme = (ruleType: string) => {
      switch(ruleType.toLowerCase()) {
        case 'not_null':
          return {
            title: 'Data Completeness Analysis',
            description: 'Checks for missing or null values in fields',
            bgClass: 'bg-green-50',
            borderClass: 'border-green-200',
            headerBgClass: 'bg-green-100',
            titleClass: 'text-green-900'
          };
        case 'uniqueness':
          return {
            title: 'Uniqueness Analysis',
            description: 'Validates uniqueness constraints on fields',
            bgClass: 'bg-blue-50',
            borderClass: 'border-blue-200',
            headerBgClass: 'bg-blue-100',
            titleClass: 'text-blue-900'
          };
        case 'length':
          return {
            title: 'Length Validation',
            description: 'Ensures field values meet length requirements',
            bgClass: 'bg-yellow-50',
            borderClass: 'border-yellow-200',
            headerBgClass: 'bg-yellow-100',
            titleClass: 'text-yellow-900'
          };
        case 'range':
          return {
            title: 'Range Validation',
            description: 'Validates numeric values fall within expected ranges',
            bgClass: 'bg-purple-50',
            borderClass: 'border-purple-200',
            headerBgClass: 'bg-purple-100',
            titleClass: 'text-purple-900'
          };
        case 'allowed_values':
          return {
            title: 'Allowed Values Validation',
            description: 'Ensures values match predefined allowed lists',
            bgClass: 'bg-orange-50',
            borderClass: 'border-orange-200',
            headerBgClass: 'bg-orange-100',
            titleClass: 'text-orange-900'
          };
        case 'data_type':
          return {
            title: 'Data Type Validation',
            description: 'Verifies fields contain expected data types',
            bgClass: 'bg-indigo-50',
            borderClass: 'border-indigo-200',
            headerBgClass: 'bg-indigo-100',
            titleClass: 'text-indigo-900'
          };
        case 'profiling_stats':
          return {
            title: 'Advanced Profiling Statistics',
            description: 'Statistical analysis including outliers and distributions',
            bgClass: 'bg-pink-50',
            borderClass: 'border-pink-200',
            headerBgClass: 'bg-pink-100',
            titleClass: 'text-pink-900'
          };
        default:
          return {
            title: `${ruleType.replace(/_/g, ' ')} Checks`,
            description: 'Additional profiling checks',
            bgClass: 'bg-gray-50',
            borderClass: 'border-gray-200',
            headerBgClass: 'bg-gray-100',
            titleClass: 'text-gray-900'
          };
      }
    };

    // Helper function to render a rule-specific table
    const renderRuleTable = (ruleType: string, rules: any[]) => {
      const theme = getRuleTypeTheme(ruleType);

      // Skip rendering if no valid rules
      if (!rules || rules.length === 0) {
        return null;
      }

      return (
        <div key={ruleType} className={`bg-white border ${theme.borderClass} rounded-lg overflow-hidden mt-6`}>
          <div className={`px-6 py-4 border-b ${theme.borderClass} ${theme.headerBgClass}`}>
            <h3 className={`text-lg font-semibold ${theme.titleClass}`}>{theme.title}</h3>
            <p className="text-sm text-gray-600">{theme.description}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Field Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Records
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valid Records
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Compliance %
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rules.map((rule: any, index: number) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {rule.Field_Name || 'Unknown Field'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {rule.Total_Records !== undefined ? rule.Total_Records : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {rule.Valid_Records !== undefined ? rule.Valid_Records : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {rule.Compliance_Percentage !== undefined ? rule.Compliance_Percentage : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        rule.Score === 'Excellent' ? 'bg-green-100 text-green-800' :
                        rule.Score === 'Fair' ? 'bg-yellow-100 text-yellow-800' :
                        rule.Score === 'Poor' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {rule.Score || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {rule.Rationale || 'No details available'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    };

    return (
      <div className="space-y-6">
        {/* Basic Profiling Table */}
        {profilingData.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Basic Profiling Metrics</h3>
              <p className="text-sm text-gray-600">General field statistics and completeness analysis</p>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Field Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Records
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valid Records
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Unique Records
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Missing %
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quality Score
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {profilingData.map((field: any, index: number) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {field.Master_Data_Item}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {field.Total_Records}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {field.Valid_Records}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {field.Unique_Records}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {field.Missing_Percentage}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          field.Score === 'Excellent' ? 'bg-green-100 text-green-800' :
                          field.Score === 'Fair' ? 'bg-yellow-100 text-yellow-800' :
                          field.Score === 'Poor' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {field.Score}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Rule-specific Tables */}
        {Object.keys(ruleGroups).length > 0 ? (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Detailed Profiling Checks</h3>
              <p className="text-sm text-blue-700">
                The following tables show detailed rule-specific profiling results grouped by rule type.
                Each table provides compliance percentages and quality scores for different validation rules.
              </p>
            </div>

            {Object.entries(ruleGroups).map(([ruleType, rules]) =>
              renderRuleTable(ruleType, rules)
            )}
          </>
        ) : (
          profilingData.length > 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mt-6">
              <div className="text-center">
                <InformationCircleIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Enhanced Profiling Rules</h3>
                <p className="text-gray-500">
                  No rule-specific profiling data is available. Configure profiling rules in the data quality configuration to see enhanced profiling results.
                </p>
              </div>
            </div>
          )
        )}
      </div>
    );
  };

  const renderStructural = (reportData: any) => {
    const structural = reportData.structural || {};
    const structuralData = structural.data || [];

    // Group checks by type
    const criticalChecks = structuralData.filter((check: any) => check.Check_Type === 'Critical');
    const structuralChecks = structuralData.filter((check: any) => check.Check_Type === 'Structural');

    return (
      <div className="space-y-6">
        {/* Critical Issues */}
        {criticalChecks.length > 0 && (
          <div className="bg-white border border-red-200 rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-red-200 bg-red-50">
              <h3 className="text-lg font-semibold text-red-900">Critical Issues ({criticalChecks.length})</h3>
              <p className="text-sm text-red-700">These issues require immediate attention</p>
            </div>
            <div className="p-6 space-y-4">
              {criticalChecks.map((check: any, index: number) => (
                <div key={index} className="border border-red-200 rounded-lg p-4 bg-red-50">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-red-900">{check.Data_Quality_Checkpoint}</h4>
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold">
                      {check.Number_of_Cases} cases
                    </span>
                  </div>
                  <p className="text-sm text-red-700 mb-2">{check.Why_It_Matters}</p>
                  <p className="text-sm text-red-600">{check.Rationale}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Structural Checks */}
        {structuralChecks.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Structural Checks ({structuralChecks.length})</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Checkpoint
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cases
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Score
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {structuralChecks.map((check: any, index: number) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {check.Data_Quality_Checkpoint}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {check.Number_of_Cases}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          check.Score === 'Excellent' ? 'bg-green-100 text-green-800' :
                          check.Score === 'Fair' ? 'bg-yellow-100 text-yellow-800' :
                          check.Score === 'Poor' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {check.Score}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {check.Rationale}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Summary Stats */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Structural Analysis Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-600">Structural Checks</p>
              <p className="text-2xl font-bold text-blue-600">{structuralChecks.length}</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-600">Total Checks</p>
              <p className="text-2xl font-bold text-green-600">{structuralData.length}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDescription = (reportData: any) => {
    const description = reportData.description || {};
    const descriptionData = description.data || [];

    return (
      <div className="space-y-6">
        {/* Description Table */}
        {descriptionData.length > 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Description Data</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Check Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data Quality Checkpoint
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cases Found
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Score
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rationale
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {descriptionData.map((check: any, index: number) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          check.Check_Type === 'Critical' ? 'bg-red-100 text-red-800' :
                          check.Check_Type === 'Structural' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {check.Check_Type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {check.Data_Quality_Checkpoint}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {check.Number_of_Cases}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          check.Score === 'Excellent' ? 'bg-green-100 text-green-800' :
                          check.Score === 'Fair' ? 'bg-yellow-100 text-yellow-800' :
                          check.Score === 'Poor' ? 'bg-red-100 text-red-800' :
                          check.Score === 'Critical' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {check.Score}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {check.Rationale}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="text-center">
              <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Description Data Available</h3>
              <p className="text-gray-500 mb-4">
                Description quality checks data is not available for this report.
              </p>
            </div>
          </div>
        )}

        {/* Why It Matters Section */}
        {descriptionData.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Why These Checks Matter</h3>
            <div className="space-y-3">
              {descriptionData.filter((check: any) => check.Why_It_Matters).map((check: any, index: number) => (
                <div key={index} className="border-l-4 border-blue-400 pl-4">
                  <p className="font-medium text-gray-900">{check.Data_Quality_Checkpoint}</p>
                  <p className="text-sm text-gray-600">{check.Why_It_Matters}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderRawData = (reportData: any) => {
    return (
      <div className="space-y-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Raw Report Data</h3>
          <pre className="bg-gray-50 p-4 rounded-lg text-sm overflow-auto max-h-96">
            {JSON.stringify(reportData, null, 2)}
          </pre>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-5/6 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Profiling Report</h2>
            <p className="text-sm text-gray-500">Job ID: {jobId}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar - Report List */}
          <div className="w-80 border-r border-gray-200 bg-gray-50">
            <div className="p-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Available Reports</h3>
              {loading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">Loading...</p>
                </div>
              ) : error ? (
                <div className="text-center py-4">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              ) : reports.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-500">No reports found</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {reports.map((report) => (
                    <button
                      key={report.id}
                      onClick={() => loadFullReport(report.id)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        selectedReport?.id === report.id
                          ? 'bg-blue-50 border-blue-200'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-900">
                          {report.table_name}
                        </span>
                        {getStatusIcon(report.status)}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${getStatusColor(report.status)}`}>
                          {report.status}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(report.started_at).toLocaleDateString()}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col">
            {/* Tabs */}
            {selectedReport && (
              <div className="border-b border-gray-200">
                <nav className="flex space-x-8 px-6">
                  {[
                    { id: 'summary', name: 'Summary', icon: ChartBarIcon },
                    { id: 'profiling', name: 'Profiling', icon: TableCellsIcon },
                    { id: 'structural', name: 'Structural', icon: InformationCircleIcon },
                    { id: 'description', name: 'Description', icon: DocumentTextIcon },
                    { id: 'dqr', name: 'DQR Report', icon: ClipboardDocumentListIcon }

//                     { id: 'raw', name: 'Raw Data', icon: DocumentTextIcon }
                  ].map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                          activeTab === tab.id
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {tab.name}
                      </button>
                    );
                  })}
                </nav>
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-auto p-6">
              {renderReportContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilingReport;
